// /components/Toolbar.js
import { useState, useEffect } from 'react';  
import { LayoutGrid } from 'lucide-react';
import { usePanelContext, PANEL_COMPONENTS } from './contexts/PanelContext';
import { useTheme } from './contexts/ThemeContext';
import { useWorkspaces } from './contexts/WorkspacesContext';
import { getCurrentUser } from './utils/authService';

const Toolbar = ({ 
  onDeleteNote,
  onAddNote,
  onAddDocument, 
  onToggleImageUpload,
  onSidebarContentChange,
  activeContent,
  onContainerSwap,
  onSaveNote,
  onOpenSettings,
  onOpenAITest,
    onAddSticky,        // ADD THIS
  onCycleArrange,     // ADD THIS
  arrangementMode ,    // ADD THIS
  onOpenAIModal,      // ADD THIS
  onFreezeLayout,      // ADD THIS
  selectedStickyCount = 0 
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const { theme } = useTheme();
  const { 
    changeRightPanelContent, 
    swapPanels, 
    PANEL_COMPONENTS, 
    setMainPanelContent, 
    mainPanelContent,
    isRightPanelVisible,
    toggleRightPanel,
    isLeftPanelVisible,
    toggleLeftPanel
  } = usePanelContext();

  // Token and sync status
const { syncStatus } = useWorkspaces();
const [tokenData, setTokenData] = useState({ balance: 0, used: 0, limit: 1000000 });


// Fetch token data
useEffect(() => {
  const fetchTokenData = async () => {
    const user = getCurrentUser();
    if (!user) return;
    
    try {
      const response = await fetch(`/api/tokens/balance/${user.user_id}`, {
        credentials: 'include'
      });
      const balance = await response.json();
      
      setTokenData({
        balance: balance.current_balance,
        used: balance.total_used,
        limit: 1000000
      });
    } catch (error) {
      console.error('Error fetching token data:', error);
    }
  };

  fetchTokenData();
}, []);


// Calculate token usage
const usagePercent = Math.min((tokenData.used / tokenData.limit) * 100, 100);
const getUsageColor = (percent) => {
  if (percent < 60) return '#22c55e';
  if (percent < 80) return '#f59e0b';
  return '#ef4444';
};

  const handleMouseDown = () => {
    setIsPressed(true);
  };

  const handleMouseUp = () => {
    setIsPressed(false);
    onOpenSettings();
  };

 const [showFreezeFeedback, setShowFreezeFeedback] = useState(false); 

  // Inline styles using theme variables
const toolbarStyles = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  backgroundColor: theme.bgToolbar || theme.mainPanelHeaderBg,
  borderBottom: `1px solid ${theme.mainPanelBorder}`,
  borderTop: `1px solid ${theme.mainPanelBorder}`,     // Add this line
  borderLeft: `1px solid ${theme.mainPanelBorder}`,    // Add this line  
  borderRight: `1px solid ${theme.mainPanelBorder}`,   // Add this line
  gap: '8px',
  height: '48px',
  position: 'relative',
  zIndex: 1000
};

  const toolbarLeftStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const toolButtonStyles = {
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    filter: 'grayscale(2) brightness(0.9)',  // ADD THIS - makes images monochrome and darker
  opacity: 0.85  // ADD THIS - slightly transparent
  };

  const toolButtonActiveStyles = {
    ...toolButtonStyles,
    backgroundColor: theme.mainPanelAccent,
    transform: 'scale(1.1)'
  };

  const toolButtonInactiveStyles = {
    ...toolButtonStyles,
    opacity: 0.7
  };

  const toolButtonHoverStyles = {
    backgroundColor: theme.mainPanelButtonHover,
    transform: 'scale(1.05)'
  };

  const statusContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginLeft: 'auto'  // Push to far right
};

const syncStatusStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingRight: '16px',
  borderRight: `1px solid ${theme.mainPanelBorder}`
};

const syncBarStyles = {
  width: '80px',
  height: '8px',
  background: 'rgba(0, 0, 0, 0.1)',
  borderRadius: '4px',
  overflow: 'hidden',
  position: 'relative'
};

const syncFillStyles = {
  height: '100%',
  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
  borderRadius: '4px',
  transition: 'width 0.3s ease',
  minWidth: '2px'
};

const syncTextStyles = {
  fontSize: '10px',
  fontWeight: '600',
  color: '#666',
  whiteSpace: 'nowrap'
};

const syncDetailStyles = {
  fontSize: '9px',
  fontFamily: 'monospace',
  color: '#888',
  whiteSpace: 'nowrap'
};

const syncCompleteStyles = {
  fontSize: '10px',
  fontWeight: '600',
  color: '#22c55e',
  whiteSpace: 'nowrap'
};

const tokenUsageStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
};

const usageBarStyles = {
  width: '80px',
  height: '8px',
  background: 'rgba(0, 0, 0, 0.1)',
  borderRadius: '4px',
  overflow: 'hidden',
  position: 'relative'
};

const usageTextStyles = {
  fontSize: '10px',
  fontWeight: '600',
  color: '#666',
  whiteSpace: 'nowrap'
};

const tokenDetailStyles = {
  fontSize: '9px',
  fontFamily: 'monospace',
  color: '#888',
  whiteSpace: 'nowrap'
};



  return (
    <div style={toolbarStyles}>
      {/* Left group - Document management controls */}
      <div style={toolbarLeftStyles}>
        {/* Left Panel Toggle - NEW */}

     <img
            src="/images/vert.png"
            style={{ width: '24px', height: '24px' }}
          />


        <img
          src={isLeftPanelVisible ? '/images/left-panel-on.png' : '/images/left-panel-off.png'}
          alt={isLeftPanelVisible ? 'Hide Left Panel' : 'Show Left Panel'}
          title={isLeftPanelVisible ? 'Hide Left Panel' : 'Show Left Panel'}
          style={toolButtonStyles}
          onClick={toggleLeftPanel}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        <img
          src="/images/addworkspace.png"
          alt="New Workspace"
          title="Add New Workspace"
          style={toolButtonStyles}
          onClick={onAddNote}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />

      
                            
        {/* <img
          src="/images/image-upload.png"
          alt="Toggle Image Upload"
          style={toolButtonStyles}
          onClick={() => onToggleImageUpload()}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        /> */}
        
        <img
          src="/images/settings.png"
          alt="Settings"
          title="Open Settings"
          style={toolButtonStyles}
          onClick={onOpenSettings}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        

        
        {/* <img
          src="/images/gallery.png"
          alt="Toggle Gallery"
          style={
            activeContent === 'gallery' 
              ? toolButtonActiveStyles 
              : toolButtonStyles
          }
          onClick={() => {
            onSidebarContentChange('gallery');
            changeRightPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.IMAGE_GALLERY);
          }}
          onMouseEnter={(e) => {
            if (activeContent !== 'gallery') {
              e.target.style.backgroundColor = theme.mainPanelButtonHover;
              e.target.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeContent !== 'gallery') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }
          }}
        /> */}
        
        {/* <img
          src="/images/sticky-notes.png"
          alt="Show Stickies" 
          style={
            activeContent === 'stickies' 
              ? toolButtonActiveStyles 
              : toolButtonInactiveStyles
          }
          onClick={() => {
            onSidebarContentChange('stickies');
            changeRightPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.STICKY_NOTES);
          }}
          onMouseEnter={(e) => {
            if (activeContent !== 'stickies') {
              e.target.style.backgroundColor = theme.mainPanelButtonHover;
              e.target.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeContent !== 'stickies') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }
          }}
        /> */}
        

        
        <img
          src="/images/resize.png"
          alt="Swap Content"
          title="Expand Document"
          style={toolButtonStyles}
          onClick={() => {
            console.log('Swap button clicked in Toolbar');
            swapPanels();
            if (onContainerSwap) {
              console.log('Calling onContainerSwap');
              onContainerSwap();
            }
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />

         <img
            src="/images/vert.png"
            style={{ width: '24px', height: '24px' }}
          />

             <img
          src="/images/add-document.png"
          alt="New Document"
          title="Add New Document"
          style={toolButtonStyles}
          onClick={onAddDocument}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        

        
          <img
            src="/images/remove.png"
            alt="Delete Document"
            title="Delete Document"  // ADD TOOLTIP
            style={toolButtonStyles}
            onClick={onDeleteNote}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#fee'; // Light red background
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          />

        {/* Add Sticky Button */}
        <img
          src="/images/addnotes.png"
          alt="New Note"
          title="Add New Note"
          style={toolButtonStyles}
          onClick={onAddSticky}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        {/* Arrange Button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            transition: 'all 0.2s ease'
          }}
          onClick={onCycleArrange}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.mainPanelButtonHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <img
            src="/images/arrange.png"
            alt="Arrange"
            title={`Arrangement Mode: ${arrangementMode}`}
            style={{ width: '24px', height: '24px' }}
          />


          <span style={{ 
            fontSize: '12px', 
            fontWeight: '500',
            color: theme.mainPanelText 
          }}>
            {arrangementMode}
          </span>


        </div>
                                     <img
            src="/images/vert.png"
            style={{ width: '24px', height: '24px' }}
          />
        
        {/* Right Panel Toggle */}
        {/* <img
          src={isRightPanelVisible ? '/images/toggle-on.png' : '/images/toggle-off.png'}
          alt={isRightPanelVisible ? 'Hide Right Panel' : 'Show Right Panel'}
          style={toolButtonStyles}
          onClick={toggleRightPanel}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        /> */}
      </div>

{/* AI Composition Button */}
{/* AI Composition Button */}
<div style={{ position: 'relative', display: 'inline-block' }}>
  <span
    style={{
      ...toolButtonStyles,
      fontSize: '24px'
    }}
    onClick={onOpenAIModal}
    title="Open AI Composer"
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = theme.mainPanelButtonHover;
      e.target.style.transform = 'scale(1.05)';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.transform = 'scale(1)';
    }}
  >
    🤖
  </span>
  
  {selectedStickyCount > 0 && (
    <div style={{
      position: 'absolute',
      top: '-5px',
      right: '-5px',
      backgroundColor: '#ef4444',
      color: 'white',
      borderRadius: '50%',
      width: '18px',
      height: '18px',
      fontSize: '11px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontWeight: 'bold',
      pointerEvents: 'none'
    }}>
      {selectedStickyCount}
    </div>
  )}
</div>

{/* Freeze/Save Layout Button */}
{/* Freeze/Save Layout Button */}
<span
  style={{
    ...toolButtonStyles,
    fontSize: '24px',
    backgroundColor: showFreezeFeedback ? '#4CAF50' : 'transparent',
    transform: showFreezeFeedback ? 'scale(1.2)' : 'scale(1)',
    transition: 'all 0.3s ease'
  }}
  onClick={() => {
    onFreezeLayout();
    setShowFreezeFeedback(true);
    setTimeout(() => setShowFreezeFeedback(false), 2000); // Show for 2 seconds
  }}
  title="Save Layout"
  onMouseEnter={(e) => {
    if (!showFreezeFeedback) {
      e.target.style.backgroundColor = theme.mainPanelButtonHover;
      e.target.style.transform = 'scale(1.05)';
    }
  }}
  onMouseLeave={(e) => {
    if (!showFreezeFeedback) {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.transform = 'scale(1)';
    }
  }}
>
  {showFreezeFeedback ? '✓' : '❄️'}
</span>

{/* Status displays - far right */}
      <div style={statusContainerStyles}>
        {/* Sync Status */}
        {syncStatus?.isTrusted && (
          <div style={syncStatusStyles}>
            {syncStatus.isSyncing ? (
              <>
                <div style={syncBarStyles}>
                  <div 
                    style={{
                      ...syncFillStyles,
                      width: `${Math.min((syncStatus.progress / syncStatus.total) * 100, 100)}%`
                    }}
                  />
                </div>
                <span style={syncTextStyles}>
                  🔥 {syncStatus.phase || 'Syncing...'}
                </span>
                <span style={syncDetailStyles}>
                  {syncStatus.progress}/{syncStatus.total}
                </span>
              </>
            ) : (
              <span style={syncCompleteStyles}>✓ Cached</span>
            )}
          </div>
        )}
        
        {/* Token Usage */}
        <div style={tokenUsageStyles}>
          <div style={usageBarStyles}>
            <div 
              style={{
                height: '100%',
                borderRadius: '4px',
                transition: 'all 0.3s ease',
                minWidth: '2px',
                width: `${usagePercent}%`,
                backgroundColor: getUsageColor(usagePercent)
              }}
            />
          </div>
          <span style={usageTextStyles}>
            AI: {Math.round(usagePercent)}%
          </span>
          <span style={tokenDetailStyles}>
            {tokenData.used.toLocaleString()}/{tokenData.limit.toLocaleString()}
          </span>
        </div>
      </div>

    </div>
  );
};

export default Toolbar;